import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { usePrepareCart } from '../../../../../../hooks/usePrepareCart'
import type { CreateCardPayload } from '../../../../../../services/cards'

interface CardAddFormProps {
  onSubmit: (data: CardFormData) => void
  onCancel: () => void
  shippingAddress?: Partial<{
    street: string
    complement: string
    cep: string
    city: string
    state: string
  }>
}

export interface CardFormData {
  number: string
  expiry: string
  cvv: string
  holder: string
  cpf: string
  useSameAddress: boolean
  address: string
  complement: string
  cep: string
  city: string
  state: string
}

export const toCreateCardPayload = (data: CardFormData): CreateCardPayload => {
  const [mm, yyyy] = (data.expiry || '').split('/')
  return {
    number: data.number.replace(/\D/g, ''),
    holder_name: data.holder,
    holder_document: data.cpf.replace(/\D/g, ''),
    exp_month: Number(mm) || 1,
    exp_year: Number(yyyy?.slice(-2)) || 30,
    cvv: data.cvv,
    billing_address: {
      line_1: data.address,
      line_2: data.complement,
      zip_code: data.cep.replace(/\D/g, ''),
      city: data.city,
      state: data.state,
      country: 'BR'
    },
    selected_default_card: false
  }
}

export const CardAddForm: React.FC<CardAddFormProps> = ({ onSubmit, onCancel, shippingAddress }) => {
  const { lastOrderCreated } = usePrepareCart()
  const formatCardNumber = (value: string): string => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 19)
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim()
  }

  const luhnValid = (num: string): boolean => {
    const digits = (num || '').replace(/\D/g, '')
    if (digits.length < 13 || digits.length > 19) return false
    let sum = 0
    let dbl = false
    for (let i = digits.length - 1; i >= 0; i -= 1) {
      let d = parseInt(digits[i], 10)
      if (dbl) {
        d *= 2
        if (d > 9) d -= 9
      }
      sum += d
      dbl = !dbl
    }
    return sum % 10 === 0
  }

  const formatExpiry = (value: string): string => {
    const digits = value.replace(/\D/g, '')

    if (digits.length === 0) return ''

    if (digits.length === 1) return digits

    if (digits.length === 2) return `${digits}/`

    if (digits.length >= 3) {
      const mm = digits.slice(0, 2)
      const yyyy = digits.slice(2, 6)
      return `${mm}/${yyyy}`
    }

    return digits
  }

  const isFutureExpiry = (value: string): boolean => {
    const match = /^(0[1-9]|1[0-2])\/(\d{4})$/u.exec(value || '')
    if (!match) return false
    const month = parseInt(match[1], 10)
    const year = parseInt(match[2], 10)
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    if (year > currentYear) return true

    if (year === currentYear && month >= currentMonth) return true

    return false
  }

  const formatCpf = (value: string): string => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 11)
    const p1 = digits.slice(0, 3)
    const p2 = digits.slice(3, 6)
    const p3 = digits.slice(6, 9)
    const p4 = digits.slice(9, 11)
    let out = p1
    if (p2) out += `.${p2}`
    if (p3) out += `.${p3}`
    if (p4) out += `-${p4}`
    return out
  }

  const formatCep = (value: string): string => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 8)
    if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`
    return digits
  }
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm<CardFormData>({
    defaultValues: {
      number: '',
      expiry: '',
      cvv: '',
      holder: '',
      cpf: '',
      useSameAddress: true,
      address: '',
      complement: '',
      cep: '',
      city: '',
      state: ''
    }
  })

  const form = watch()
  const submit = (data: CardFormData) => onSubmit(data)

  const handleExpiryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const target = e.target as HTMLInputElement
      const cursorPosition = target.selectionStart || 0
      const { value } = target

      if (cursorPosition === 3 && value.length === 3 && value.includes('/')) {
        e.preventDefault()
        const newValue = value.slice(0, 2)
        target.value = newValue
        setValue('expiry', newValue)
        target.setSelectionRange(2, 2)
      }
    }
  }

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const masked = formatExpiry(value)
    e.target.value = masked
    setValue('expiry', masked)
  }

  const watchedCep = watch('cep')
  const useSameAddress = watch('useSameAddress')

  const orderAddress = React.useMemo(
    () => ({
      street: lastOrderCreated?.address || lastOrderCreated?.street || '',
      complement: lastOrderCreated?.complement || '',
      cep: lastOrderCreated?.cep || '',
      city: lastOrderCreated?.city || '',
      state: lastOrderCreated?.state || ''
    }),
    [lastOrderCreated]
  )

  useEffect(() => {
    if (useSameAddress) {
      return () => {}
    }

    const cepOnlyNumbers = (watchedCep || '').replace(/\D/g, '')
    if (cepOnlyNumbers.length !== 8) {
      return () => {}
    }

    const controller = new AbortController()
    ;(async () => {
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cepOnlyNumbers}/json/`, { signal: controller.signal })
        if (!res.ok) return
        const data = await res.json()
        if (data?.erro) {
          setError('cep', { type: 'manual', message: 'CEP inválido.' })
          return
        }
        clearErrors('cep')
        if (data?.logradouro) setValue('address', data.logradouro)
        if (data?.bairro) setValue('complement', data.bairro)
        if (data?.localidade) setValue('city', data.localidade)
        if (data?.uf) setValue('state', data.uf)
      } catch (err: unknown) {
        const name = err && typeof err === 'object' && 'name' in err ? String((err as { name?: string }).name) : ''
        if (name === 'AbortError') return
        setError('cep', { type: 'manual', message: 'CEP inválido.' })
      }
    })()
    return () => {
      controller.abort()
    }
  }, [watchedCep, setValue, useSameAddress, setError, clearErrors])

  useEffect(() => {
    if (useSameAddress) {
      const source = shippingAddress || orderAddress
      const addressData = {
        address: source.street || '',
        complement: source.complement || '',
        cep: source.cep || '',
        city: source.city || '',
        state: source.state || ''
      }

      setValue('address', addressData.address)
      setValue('complement', addressData.complement)
      setValue('cep', addressData.cep)
      setValue('city', addressData.city)
      setValue('state', addressData.state)
    } else {
      setValue('address', '')
      setValue('complement', '')
      setValue('cep', '')
      setValue('city', '')
      setValue('state', '')
    }
  }, [useSameAddress, setValue, shippingAddress, orderAddress])

  return (
    <div className="space-y-6 rounded-lg border-t border-gray-200 bg-gray-50 p-6">
      <div className="mb-5">
        <h3 className="mb-2 flex items-center text-sm font-semibold text-primary-700">Adicionar cartão</h3>
        <p className="text-sm text-neutral-800">Certo! Preencha os dados do cartão, por favor</p>
      </div>

      <div className="relative aspect-[330/192.76] w-full max-w-[330px] rounded-[17px] bg-[#A69E89] p-4 font-mono text-white">
        <div className="absolute left-3 top-2 text-lg font-bold">•••</div>

        <div className="absolute bottom-[48px] left-4 right-4 flex justify-between text-xs tracking-wide">
          <span className="text-base">{form.holder || 'Nome do titular'}</span>
          <span className="text-base">{form.expiry || 'MM/AA'}</span>
        </div>

        <div className="absolute bottom-3 left-4 text-base tracking-[0.3em]">
          {form.number ? form.number.replace(/\d{4}(?=\d)/g, '•••• ') : '•••• •••• •••• ••••'}
        </div>
      </div>

      <div className="flex flex-col space-y-5">
        <div className="flex flex-col md:hidden">
          <span className="mb-1 text-sm text-neutral-500">
            Nº do cartão<span className="text-error-700">*</span>
          </span>
          <input
            {...register('number', {
              required: 'Informe o número do cartão',
              validate: {
                formato: (v) => /^[\d ]{13,23}$/u.test((v || '').trim()) || 'Formato inválido',
                luhn: (v) => luhnValid(v || '') || 'Número de cartão inválido'
              }
            })}
            placeholder="0000 0000 0000 0000"
            style={{
              border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
              background: 'var(--Secondary-Secondary100, #EDECE7)'
            }}
            inputMode="numeric"
            maxLength={23}
            className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
            autoComplete="cc-number"
            onChange={(e) => {
              const masked = formatCardNumber(e.target.value)
              e.target.value = masked
              setValue('number', masked)
            }}
          />
          {errors.number && <span className="mt-1 text-xs text-red-600">{errors.number.message}</span>}
        </div>

        <div className="flex w-full gap-3 md:hidden">
          <div className="flex flex-1 flex-col">
            <span className="mb-1 text-sm text-neutral-500">
              Validade<span className="text-red-500">*</span>
            </span>
            <input
              {...register('expiry', {
                required: 'Informe a validade',
                validate: {
                  formato: (v) => /^(0[1-9]|1[0-2])\/\d{4}$/u.test(v || '') || 'MM/AAAA',
                  futuro: (v) => isFutureExpiry(v || '') || 'Cartão vencido'
                }
              })}
              placeholder="MM/AAAA"
              style={{
                border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                background: 'var(--Secondary-Secondary100, #EDECE7)'
              }}
              inputMode="numeric"
              maxLength={7}
              className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              autoComplete="cc-exp"
              onChange={handleExpiryChange}
              onKeyDown={handleExpiryKeyDown}
            />
            {errors.expiry && <span className="mt-1 text-xs text-red-600">{errors.expiry.message}</span>}
          </div>

          <div className="flex flex-1 flex-col">
            <span className="mb-1 text-sm text-neutral-500">
              CVV<span className="text-red-500">*</span>
            </span>
            <input
              {...register('cvv', {
                required: 'Informe o CVV',
                pattern: { value: /^\d{3,4}$/u, message: 'CVV inválido' }
              })}
              placeholder="Ex. 123"
              style={{
                border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                background: 'var(--Secondary-Secondary100, #EDECE7)'
              }}
              inputMode="numeric"
              maxLength={4}
              className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              autoComplete="cc-csc"
              onChange={(e) => {
                const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 4)
                e.target.value = digits
                setValue('cvv', digits)
              }}
            />
            {errors.cvv && <span className="mt-1 text-xs text-red-600">{errors.cvv.message}</span>}
          </div>
        </div>

        {/* Layout original para desktop */}
        <div className="hidden w-full gap-5 md:flex">
          <div className="flex flex-[2] flex-col">
            <span className="mb-1 text-sm text-neutral-500">
              Nº do cartão<span className="text-error-700">*</span>
            </span>
            <input
              {...register('number', {
                required: 'Informe o número do cartão',
                validate: {
                  formato: (v) => /^[\d ]{13,23}$/u.test((v || '').trim()) || 'Formato inválido',
                  luhn: (v) => luhnValid(v || '') || 'Número de cartão inválido'
                }
              })}
              placeholder="0000 0000 0000 0000"
              style={{
                border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                background: 'var(--Secondary-Secondary100, #EDECE7)'
              }}
              inputMode="numeric"
              maxLength={23}
              className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              autoComplete="cc-number"
              onChange={(e) => {
                const masked = formatCardNumber(e.target.value)
                e.target.value = masked
                setValue('number', masked)
              }}
            />
            {errors.number && <span className="mt-1 text-xs text-red-600">{errors.number.message}</span>}
          </div>

          <div className="flex flex-[1] flex-col">
            <span className="mb-1 text-sm text-neutral-500">
              Validade<span className="text-red-500">*</span>
            </span>
            <input
              {...register('expiry', {
                required: 'Informe a validade',
                validate: {
                  formato: (v) => /^(0[1-9]|1[0-2])\/\d{4}$/u.test(v || '') || 'MM/AAAA',
                  futuro: (v) => isFutureExpiry(v || '') || 'Cartão vencido'
                }
              })}
              placeholder="MM/AAAA"
              style={{
                border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                background: 'var(--Secondary-Secondary100, #EDECE7)'
              }}
              inputMode="numeric"
              maxLength={7}
              className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              autoComplete="cc-exp"
              onChange={handleExpiryChange}
              onKeyDown={handleExpiryKeyDown}
            />
            {errors.expiry && <span className="mt-1 text-xs text-red-600">{errors.expiry.message}</span>}
          </div>

          <div className="flex flex-[0.6] flex-col">
            <span className="mb-1 text-sm text-neutral-500">
              CVV<span className="text-red-500">*</span>
            </span>
            <input
              {...register('cvv', {
                required: 'Informe o CVV',
                pattern: { value: /^\d{3,4}$/u, message: 'CVV inválido' }
              })}
              placeholder="Ex. 123"
              style={{
                border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                background: 'var(--Secondary-Secondary100, #EDECE7)'
              }}
              inputMode="numeric"
              maxLength={4}
              className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              autoComplete="cc-csc"
              onChange={(e) => {
                const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 4)
                e.target.value = digits
                setValue('cvv', digits)
              }}
            />
            {errors.cvv && <span className="mt-1 text-xs text-red-600">{errors.cvv.message}</span>}
          </div>
        </div>

        {/* Nome do titular - linha inteira no mobile */}
        <div className="flex flex-col md:hidden">
          <span className="mb-1 text-sm text-neutral-500">
            Nome do titular<span className="text-red-500">*</span>
          </span>
          <input
            {...register('holder', { required: 'Informe o titular' })}
            placeholder="Nome igual ao cartão"
            style={{
              border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
              background: 'var(--Secondary-Secondary100, #EDECE7)'
            }}
            className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
            autoComplete="cc-name"
            onChange={(e) => {
              setValue('holder', e.target.value)
            }}
          />
          {errors.holder && <span className="mt-1 text-xs text-red-600">{errors.holder.message}</span>}
        </div>

        {/* CPF do titular - linha inteira no mobile */}
        <div className="flex flex-col md:hidden">
          <span className="mb-1 text-sm text-neutral-500">
            CPF do titular<span className="text-red-500">*</span>
          </span>
          <input
            {...register('cpf', {
              required: 'Informe o CPF',
              pattern: { value: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/u, message: 'CPF inválido' }
            })}
            placeholder="000.000.000-00"
            style={{
              border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
              background: 'var(--Secondary-Secondary100, #EDECE7)'
            }}
            inputMode="numeric"
            maxLength={14}
            className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
            autoComplete="off"
            onChange={(e) => {
              const masked = formatCpf(e.target.value)
              e.target.value = masked
              setValue('cpf', masked)
            }}
          />
          {errors.cpf && <span className="mt-1 text-xs text-red-600">{errors.cpf.message}</span>}
        </div>

        {/* Layout original para desktop - Nome e CPF */}
        <div className="hidden w-full gap-5 md:flex">
          <div className="flex flex-[2] flex-col">
            <span className="mb-1 text-sm text-neutral-500">
              Nome do titular<span className="text-red-500">*</span>
            </span>
            <input
              {...register('holder', { required: 'Informe o titular' })}
              placeholder="Nome igual ao cartão"
              style={{
                border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                background: 'var(--Secondary-Secondary100, #EDECE7)'
              }}
              className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              autoComplete="cc-name"
            />
            {errors.holder && <span className="mt-1 text-xs text-red-600">{errors.holder.message}</span>}
          </div>

          <div className="flex flex-[1] flex-col">
            <span className="mb-1 text-sm text-neutral-500">
              CPF do titular<span className="text-red-500">*</span>
            </span>
            <input
              {...register('cpf', {
                required: 'Informe o CPF',
                pattern: { value: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/u, message: 'CPF inválido' }
              })}
              placeholder="000.000.000-00"
              style={{
                border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                background: 'var(--Secondary-Secondary100, #EDECE7)'
              }}
              inputMode="numeric"
              maxLength={14}
              className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              autoComplete="off"
              onChange={(e) => {
                const masked = formatCpf(e.target.value)
                e.target.value = masked
                setValue('cpf', masked)
              }}
            />
            {errors.cpf && <span className="mt-1 text-xs text-red-600">{errors.cpf.message}</span>}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label htmlFor="useSameAddress" className="flex cursor-pointer items-center gap-3 text-sm text-neutral-700">
            <input
              {...register('useSameAddress')}
              type="checkbox"
              id="useSameAddress"
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary-600"
              style={{ accentColor: 'var(--primary-color, #6B7B3C)' }}
            />
            Usar o mesmo endereço da etapa de entrega
          </label>
        </div>

        {!useSameAddress && (
          <>
            {/* Layout mobile - campos do endereço */}
            <div className="flex flex-col space-y-5 md:hidden">
              <div className="flex flex-col">
                <span className="mb-1 text-sm text-neutral-500">
                  Endereço de cobrança<span className="text-red-500">*</span>
                </span>
                <input
                  {...register('address', {
                    required: useSameAddress ? false : 'Informe o endereço'
                  })}
                  placeholder="Ex: Av. Paulista, 1000"
                  style={{
                    border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                    background: 'var(--Secondary-Secondary100, #EDECE7)'
                  }}
                  className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                />
                {errors.address && <span className="mt-1 text-xs text-red-600">{errors.address.message}</span>}
              </div>

              <div className="flex w-full gap-3">
                <div className="flex flex-1 flex-col">
                  <span className="mb-1 text-sm text-neutral-500">Complemento</span>
                  <input
                    {...register('complement')}
                    placeholder="Ex: Apto 04"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                  />
                </div>

                <div className="flex flex-1 flex-col">
                  <span className="mb-1 text-sm text-neutral-500">
                    CEP<span className="text-red-500">*</span>
                  </span>
                  <input
                    {...register('cep', {
                      required: useSameAddress ? false : 'Informe o CEP',
                      pattern: { value: /^\d{5}-?\d{3}$/u, message: 'CEP inválido' }
                    })}
                    placeholder="00000-000"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    inputMode="numeric"
                    maxLength={9}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                    onChange={(e) => {
                      const masked = formatCep(e.target.value)
                      e.target.value = masked
                      setValue('cep', masked)
                    }}
                  />
                  {errors.cep && <span className="mt-1 text-xs text-red-600">{errors.cep.message}</span>}
                </div>
              </div>

              <div className="flex w-full gap-3">
                <div className="flex flex-1 flex-col">
                  <span className="mb-1 text-sm text-neutral-500">
                    Cidade<span className="text-red-500">*</span>
                  </span>
                  <input
                    {...register('city', {
                      required: useSameAddress ? false : 'Informe a cidade'
                    })}
                    placeholder="Ex: São Paulo"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                  />
                  {errors.city && <span className="mt-1 text-xs text-red-600">{errors.city.message}</span>}
                </div>

                <div className="flex flex-1 flex-col">
                  <span className="mb-1 text-sm text-neutral-500">
                    Estado<span className="text-red-500">*</span>
                  </span>
                  <input
                    {...register('state', {
                      required: useSameAddress ? false : 'Informe o estado'
                    })}
                    placeholder="Ex: SP"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                  />
                  {errors.state && <span className="mt-1 text-xs text-red-600">{errors.state.message}</span>}
                </div>
              </div>
            </div>

            {/* Layout desktop - campos do endereço */}
            <div className="hidden space-y-5 md:block">
              <div className="flex w-full gap-5">
                <div className="flex flex-[3] flex-col">
                  <span className="mb-1 text-sm text-neutral-500">
                    Endereço de cobrança<span className="text-red-500">*</span>
                  </span>
                  <input
                    {...register('address', {
                      required: useSameAddress ? false : 'Informe o endereço'
                    })}
                    placeholder="Ex: Av. Paulista, 1000"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                  />
                  {errors.address && <span className="mt-1 text-xs text-red-600">{errors.address.message}</span>}
                </div>

                <div className="flex flex-[1] flex-col">
                  <span className="mb-1 text-sm text-neutral-500">Complemento</span>
                  <input
                    {...register('complement')}
                    placeholder="Ex: Apto 04"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                  />
                </div>

                <div className="flex flex-[1] flex-col">
                  <span className="mb-1 text-sm text-neutral-500">
                    CEP<span className="text-red-500">*</span>
                  </span>
                  <input
                    {...register('cep', {
                      required: useSameAddress ? false : 'Informe o CEP',
                      pattern: { value: /^\d{5}-?\d{3}$/u, message: 'CEP inválido' }
                    })}
                    placeholder="00000-000"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    inputMode="numeric"
                    maxLength={9}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                    onChange={(e) => {
                      const masked = formatCep(e.target.value)
                      e.target.value = masked
                      setValue('cep', masked)
                    }}
                  />
                  {errors.cep && <span className="mt-1 text-xs text-red-600">{errors.cep.message}</span>}
                </div>
              </div>

              <div className="flex w-full gap-5">
                <div className="flex flex-1 flex-col">
                  <span className="mb-1 text-sm text-neutral-500">
                    Cidade<span className="text-red-500">*</span>
                  </span>
                  <input
                    {...register('city', {
                      required: useSameAddress ? false : 'Informe a cidade'
                    })}
                    placeholder="Ex: São Paulo"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                  />
                  {errors.city && <span className="mt-1 text-xs text-red-600">{errors.city.message}</span>}
                </div>

                <div className="flex flex-1 flex-col">
                  <span className="mb-1 text-sm text-neutral-500">
                    Estado<span className="text-red-500">*</span>
                  </span>
                  <input
                    {...register('state', {
                      required: useSameAddress ? false : 'Informe o estado'
                    })}
                    placeholder="Ex: SP"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)'
                    }}
                    className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
                  />
                  {errors.state && <span className="mt-1 text-xs text-red-600">{errors.state.message}</span>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        <button
          onClick={handleSubmit(submit)}
          disabled={isSubmitting}
          className="w-full rounded-lg border-0 bg-[#464F31] py-2 text-sm text-white hover:bg-primary-900 disabled:opacity-60">
          {isSubmitting ? 'Adicionando...' : 'Adicionar Cartão'}
        </button>
        <button
          onClick={onCancel}
          className="w-full rounded-lg border-0 bg-transparent py-2 text-xs font-medium text-primary-800 hover:underline">
          Cancelar
        </button>
      </div>
    </div>
  )
}

export default CardAddForm
